import React, { useContext, useEffect, useState } from 'react';
import { Button, Dropdown, Menu, message } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { Task } from '../../types';
import { fetchAppointmentTypes } from '../../utils';
import { UserContext } from '../../UserContext';

// Define the shape of an appointment type
interface AppointmentType {
  id: string;
  name: string;
}

// Define the props for the AppointmentTypesDropdown component
interface AppointmentTypesDropdownProps {
  task: Task;
  apiUrl: string;
  clientId: number | undefined;
  onErrorChange: (hasError: boolean) => void;
  onAppointmentTypeModified: (
    taskId: string,
    appointmentTypeName: string,
    appointmentTypeId: string,
  ) => void;
}

const APPOINTMENT_TYPE_NONE = { id: '-1', name: 'None' };

const AppointmentTypesDropdown: React.FC<AppointmentTypesDropdownProps> = ({
  task,
  apiUrl,
  clientId,
  onErrorChange,
  onAppointmentTypeModified,
}) => {
  const user = useContext(UserContext);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>(
    [],
  );
  const [selectedAppointmentType, setSelectedAppointmentType] =
    useState<AppointmentType | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  const loadAppointmentTypes = async (clientId: number) => {
    try {
      const appointmentTypesData = await fetchAppointmentTypes(
        apiUrl,
        clientId.toString(),
        user?.accessToken,
      );
      console.log('Appointment types data:', appointmentTypesData);

      if (appointmentTypesData && appointmentTypesData.length > 0) {
        setAppointmentTypes([...appointmentTypesData, APPOINTMENT_TYPE_NONE]);
        const selectedType = appointmentTypesData.find(
          (type: AppointmentType) =>
            type.id === task.raw_task?.appointment_type_id,
        );
        setSelectedAppointmentType(selectedType || null);
        if (!selectedType) {
          setIsError(true);
          message.error('No appointment types found for the selected client.');
        } else {
          setIsError(false);
        }
      } else {
        setIsError(true);
        message.error('No appointment types found for the selected client.');
      }
    } catch (error) {
      message.error(
        'Failed to load appointment types. Please try again later.',
      );
      console.error('Error fetching appointment types:', error);
      setIsError(true);
    }
  };

  useEffect(() => {
    onErrorChange(isError);
  }, [isError, onErrorChange]);

  useEffect(() => {
    if (clientId !== undefined) {
      loadAppointmentTypes(clientId).catch(error =>
        console.error('Failed to execute appointmentTypes:', error),
      );
    }
  }, [task.raw_task?.appointment_type_id, clientId]);

  const handleMenuClick = (e: any) => {
    const selected = appointmentTypes.find(type => type.id === e.key);
    setSelectedAppointmentType(selected || null);
    setIsError(false);
    if (selected) {
      onAppointmentTypeModified(task.id, selected.name, selected.id);
    }
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      {appointmentTypes.map(type => (
        <Menu.Item key={type.id}>{type.name}</Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown overlay={menu}>
      <Button
        style={{
          borderColor: isError ? 'red' : 'initial',
          color: isError ? 'red' : 'initial',
          fontWeight: 'bold',
        }}
      >
        {selectedAppointmentType ? selectedAppointmentType.name : 'Select Type'}{' '}
        <DownOutlined />
      </Button>
    </Dropdown>
  );
};

export default AppointmentTypesDropdown;
